import { Vue, Component } from 'vue-property-decorator';

import _ from 'lodash';
import rawData from '@/data/19983881105_e93c2d8279_b.jpg.json';

interface Node {
  Ind: number;
  Type: string;
  Lind: number;
  Rind: number;
  NumLeaves: number;
  R: number;
  G: number;
  B: number;
  Ward: number;
  Sse: number;
  Var: number;
  Std: number;
  NumClusterSamples: number;
  x: number; // 노드의 x 좌표
  y: number; // 노드의 y 좌표
  parentInd: number | null; // 부모 index
  depth: number; // root 노드로부터의 depth
  leftRightInfoList: Array<'left' | 'right'>; // root 노드로 부터 왼쪽 자식인지, 오른쪽 자식인지. ['left', 'right', 'left', 'left']
  depthRowIndex: number; // 일정 depth에서의 왼쪽시작 기준으로 해당 node의 index
  height: number; // Ward(or 다른 어떤 기준)의 합
  oneUnitHeight: number; // depth마다 1단위씩 올라가는 height
  isBalanced: boolean; // 자식들이 balanced 상태인지
}

interface NodesDict {
  [nodeId: number]: Node;
}

interface Edge {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

@Component({})
export default class RadialDendrogram extends Vue {
  private svgWidth: number = 1200;
  private svgHeight: number = 1200;
  private cuttingCircleRadius: number = 380;

  private cuttingHeight: number = 100;
  private heightStandard: string = 'Ward';
  private heightStandardList: string[] = ['Ward', 'Sse', 'Var', 'Std'];
  private depthStandard: number = 1;
  private childNodesShowed: boolean = false;

  private nodes: Node[] = [];
  private edges: Edge[] = [];
  private cuttingHeightLeafNodes: Node[] = [];
  private renderingNodes: Node[] = [];

  private created() {
    this.init();
  }

  private init() {
    // nodes를 초기화한다.
    this.nodes = _.map(rawData, datum => {
      return {
        Ind: datum.Ind,
        Type: datum.Type,
        Lind: datum.Lind,
        Rind: datum.Rind,
        NumLeaves: datum.NumLeaves,
        R: datum.R,
        G: datum.G,
        B: datum.B,
        Ward: datum.Ward,
        Sse: datum.Sse,
        Var: datum.Var,
        Std: datum.Std,
        NumClusterSamples: datum.NumClusterSamples,
        x: 0,
        y: 0,
        parentInd: null,
        depth: -1,
        leftRightInfoList: [],
        depthRowIndex: -1,
        height: 0,
        oneUnitHeight: 0,
        isBalanced: true
      };
    });
    this.edges = [];
    this.cuttingHeightLeafNodes = [];
    this.renderingNodes = [];

    const nodesDict: NodesDict = _.keyBy(this.nodes, node => node.Ind);

    // parent를 구한다.
    _.forEach(this.nodes, node => {
      if (nodesDict.hasOwnProperty(node.Lind)) {
        nodesDict[node.Lind].parentInd = node.Ind;
      }
      if (nodesDict.hasOwnProperty(node.Rind)) {
        nodesDict[node.Rind].parentInd = node.Ind;
      }
    });

    // root를 구한다.
    const rootNode: Node = _.filter(
      this.nodes,
      node => node.parentInd === null
    )[0];

    // 각 node마다 depth와 leftRightInfoList를 구한다.
    for (let i = 0; i < this.nodes.length; i++) {
      this.nodes[i] = this.makeNodeDepthAndLeftRightInfoList(
        this.nodes[i],
        nodesDict
      );
    }

    // 각 node마다 height를 구한다.
    _.forEach(this.nodes, node => {
      node.height = this.makeNodeHeight(node, nodesDict);
      node.oneUnitHeight = this.makeNodeOneUnitHeight(node, nodesDict);
    });

    // cuttingHeight의 leaf nodes를 구한다.
    this.cuttingHeightLeafNodes = this.makeCuttingHeightLeafNodes(
      nodesDict,
      this.cuttingHeight
    );

    let maxHeight: number = 0;
    const maxHeightNode = _.maxBy(
      this.cuttingHeightLeafNodes,
      node => node.height
    );
    if (!_.isNil(maxHeightNode)) {
      maxHeight = maxHeightNode.height;
    }

    // cuttingHeightLeafNodes를 원에 등각도로 배치한다.
    _.forEach(this.cuttingHeightLeafNodes, (node, index) => {
      node.x =
        this.svgWidth / 2 +
        this.cuttingCircleRadius *
          Math.cos(
            ((2 * Math.PI) / this.cuttingHeightLeafNodes.length) * index
          );
      node.y =
        this.svgHeight / 2 +
        this.cuttingCircleRadius *
          Math.sin(
            ((2 * Math.PI) / this.cuttingHeightLeafNodes.length) * index
          );
      // *점선원이 height를 가리킬 때 표현하는 방법
      // node.x =
      //   this.svgWidth / 2 +
      //   (node.height / this.cuttingHeight) *
      //     this.cuttingCircleRadius *
      //     Math.cos(
      //       ((2 * Math.PI) / this.cuttingHeightLeafNodes.length) * index
      //     );
      // node.y =
      //   this.svgHeight / 2 +
      //   (node.height / this.cuttingHeight) *
      //     this.cuttingCircleRadius *
      //     Math.sin(
      //       ((2 * Math.PI) / this.cuttingHeightLeafNodes.length) * index
      //   );
    });

    // 부모들을 찾는다
    this.renderingNodes = this.cuttingHeightLeafNodes;
    for (let i = 0; i < this.depthStandard; i++) {
      // 바로 위 부모들만 반환한다
      this.renderingNodes = this.makeCuttingParentNodesAndUnion(
        this.renderingNodes,
        nodesDict,
        this.heightStandard
      );
    }

    // 자식들을 찾는다.
    // while (true) {
    // }

    // balanced인지 확인한다
    _.forEach(this.nodes, node => {
      node.isBalanced = this.isBalanced(node, nodesDict);
    });

    // console.log('this.renderingNodes', this.renderingNodes);
    console.log('this.nodes', this.nodes);

    // edges를 만든다.
    const renderingNodesHash = _.keyBy(this.renderingNodes, node => node.Ind);
    _.forEach(this.renderingNodes, node => {
      if (node.Lind !== 0 && renderingNodesHash.hasOwnProperty(node.Lind)) {
        this.edges.push({
          x1: node.x as number,
          y1: node.y as number,
          x2: nodesDict[node.Lind].x as number,
          y2: nodesDict[node.Lind].y as number
        });
      }
      if (node.Rind !== 0 && renderingNodesHash.hasOwnProperty(node.Rind)) {
        this.edges.push({
          x1: node.x as number,
          y1: node.y as number,
          x2: nodesDict[node.Rind].x as number,
          y2: nodesDict[node.Rind].y as number
        });
      }
    });
  }

  /**
   * 부모 노드들을 찾고 자식노드드과 부모노드들을 union해서 반환하는 함수
   * @param currentNodes
   * @param nodesDict
   * @param heightStandard
   */
  private makeCuttingParentNodesAndUnion(
    currentNodes: Node[],
    nodesDict: NodesDict,
    heightStandard: string
  ): Node[] {
    // 바로 위 부모만 테스트 해본다
    const parentNodesDict: { [nodeIndex: number]: Node } = {};
    const cuttingHeightLeafNodesDict = _.keyBy(currentNodes, node => node.Ind);

    _.forEach(currentNodes, node => {
      if (node.parentInd !== null) {
        const parentNode = nodesDict[node.parentInd];

        if (
          cuttingHeightLeafNodesDict.hasOwnProperty(parentNode.Lind) &&
          cuttingHeightLeafNodesDict.hasOwnProperty(parentNode.Rind)
        ) {
          parentNodesDict[node.parentInd] = nodesDict[node.parentInd];
        }
      }
    });

    _.forEach(parentNodesDict, node => {
      // 자식들의 중점을 구한다.
      // 현재 node의 Ward가 필요하다
      // 각도가 필요하다
      let leftChildNode!: Node;
      let rightChildNode!: Node;

      if (cuttingHeightLeafNodesDict.hasOwnProperty(node.Lind)) {
        leftChildNode = cuttingHeightLeafNodesDict[node.Lind];
      }

      if (cuttingHeightLeafNodesDict.hasOwnProperty(node.Rind)) {
        rightChildNode = cuttingHeightLeafNodesDict[node.Rind];
      }

      if (!_.isNil(leftChildNode) && !_.isNil(rightChildNode)) {
        const dx: number = rightChildNode.x - leftChildNode.x;
        const dy: number = rightChildNode.y - leftChildNode.y;
        const centerX: number =
          leftChildNode.x <= rightChildNode.x
            ? leftChildNode.x + Math.abs(dx) / 2
            : rightChildNode.x + Math.abs(dx) / 2;
        const centerY: number =
          leftChildNode.y <= rightChildNode.y
            ? leftChildNode.y + Math.abs(dy) / 2
            : rightChildNode.y + Math.abs(dy) / 2;

        // if (node.Ind === 280) {
        //   console.log('280 centerX', centerX);
        //   console.log('280 centerY', centerY);
        // }

        const dxdyAngle: number = this.getAngle({
          startX: leftChildNode.x,
          startY: leftChildNode.y,
          endX: rightChildNode.x,
          endY: rightChildNode.y
        });

        const centerAngle: number = dxdyAngle + Math.PI / 2;
        node.x = centerX + node[heightStandard] * Math.cos(centerAngle);
        node.y = centerY + node[heightStandard] * Math.sin(centerAngle);
      }
    });

    const parentNodes = _.values(parentNodesDict);
    const unionedNodes = _.chain(currentNodes)
      .concat(parentNodes)
      .unionBy(node => node.Ind)
      .value();

    return unionedNodes;
  }

  /**
   * 각도를 구하는 함수
   */
  private getAngle(o: {
    startX: number;
    startY: number;
    endX: number;
    endY: number;
  }): number {
    const dy: number = o.endY - o.startY;
    const dx: number = o.endX - o.startX;
    const angle: number = Math.atan2(dy, dx);

    return angle;
  }

  /**
   * 노드의 depth와 루트 기준으로 왼쪽 오른쪽자식 계보를 나타내주는 leftRightInfoList (ex. ['left', 'left', 'right', ...])
   * @param node
   * @param nodesDict
   */
  private makeNodeDepthAndLeftRightInfoList(
    node: Node,
    nodesDict: NodesDict
  ): Node {
    let currentNode: Node = node;
    let depth: number = 0;
    const leftRightInfoList: Array<'left' | 'right'> = [];

    while (currentNode.parentInd !== null) {
      depth += 1;
      const parentNode: Node = nodesDict[currentNode.parentInd];

      if (currentNode.Ind === parentNode.Lind) {
        leftRightInfoList.unshift('left');
      } else if (currentNode.Ind === parentNode.Rind) {
        leftRightInfoList.unshift('right');
      }

      currentNode = parentNode;
    }

    node.depth = depth;
    node.leftRightInfoList = leftRightInfoList;

    return node;
  }

  /**
   * cuttingHeight 기준으로 잘려진 후의 leaf node들을 구하는 함수
   * @param nodesDict
   * @param cuttingHeight
   */
  private makeCuttingHeightLeafNodes(
    nodesDict: NodesDict,
    cuttingHeight: number
  ): Node[] {
    const cuttingHeightLeafNodes: Node[] = [];

    _.forEach(nodesDict, node => {
      if (
        node.parentInd !== null &&
        cuttingHeight >= node.height &&
        cuttingHeight < nodesDict[node.parentInd].height
      ) {
        cuttingHeightLeafNodes.push(node);
      }

      if (
        node.parentInd !== null &&
        cuttingHeight <= node.height &&
        node.Type === 'leaf'
      ) {
        cuttingHeightLeafNodes.push(node);
      }
    });

    // 같은 부모가 있는 child노드들에 대한 순서 보장을 위한 sorting.
    // 일단은 되는 듯하다.
    cuttingHeightLeafNodes.sort((node1, node2) => {
      let result: number = 0;

      const standardLeftRightInfoList: Array<'left' | 'right'> =
        node1.leftRightInfoList.length <= node2.leftRightInfoList.length
          ? node1.leftRightInfoList
          : node2.leftRightInfoList;

      for (let i = 0; i < standardLeftRightInfoList.length; i++) {
        if (
          node1.leftRightInfoList[i] === 'left' &&
          node2.leftRightInfoList[i] === 'right'
        ) {
          result = -1;
          break;
        } else if (
          node1.leftRightInfoList[i] === 'right' &&
          node2.leftRightInfoList[i] === 'left'
        ) {
          result = 1;
          break;
        }
      }

      return result;
    });

    return cuttingHeightLeafNodes;
  }

  /**
   * node의 height를 구하는 함수
   * @param node
   * @param nodesDict
   */
  private makeNodeHeight(node: Node, nodesDict: NodesDict): number {
    if (!node) {
      return 0;
    }
    if (node.Lind === 0 && node.Rind === 0) {
      return 0;
    }
    return (
      node.Ward +
      Math.max(
        this.makeNodeHeight(nodesDict[node.Lind], nodesDict),
        this.makeNodeHeight(nodesDict[node.Rind], nodesDict)
      )
    );
  }

  // 해당 노드의 child 노드들이 balanced인지 확인하는 함수
  private isBalanced(node: Node, nodesDict: NodesDict): boolean {
    if (!node) {
      return true;
    }
    return (
      1 >= this.getChildrenDepthDifference(node, nodesDict) &&
      this.isBalanced(nodesDict[node.Lind], nodesDict) &&
      this.isBalanced(nodesDict[node.Rind], nodesDict)
    );
  }

  // 왼쪽자식, 오른쪽 자식의 높이차를 반환하는 함수
  private getChildrenDepthDifference(node: Node, nodesDict: NodesDict) {
    if (!node || (node.Lind === 0 && node.Rind === 0)) {
      return 0;
    }
    return Math.abs(
      nodesDict[node.Lind].oneUnitHeight - nodesDict[node.Rind].oneUnitHeight
    );
  }

  /**
   * node의 depth마다 1단위씩 증가하는 height를 구하는 함수
   * @param node
   * @param nodesDict
   */
  private makeNodeOneUnitHeight(node: Node, nodesDict: NodesDict): number {
    if (!node) {
      return 0;
    }
    if (node.Lind === 0 && node.Rind === 0) {
      return 0;
    }
    return (
      1 +
      Math.max(
        this.makeNodeOneUnitHeight(nodesDict[node.Lind], nodesDict),
        this.makeNodeOneUnitHeight(nodesDict[node.Rind], nodesDict)
      )
    );
  }

  private mounted() {
    // //
    // console.log(
    //   'angle',
    //   this.getAngle({
    //     startX: 0,
    //     startY: 0,
    //     endX: 0,
    //     endY: -1
    //   })
    // );
  }
}
